import { createContext, useContext, useEffect, useState } from "react";

import {
  BehaviorKit,
  CompaniesApi,
  CompanyWithBlueprintInfo,
} from "../api/Companies";
import {
  CompaniesWithBlueprintInput,
  useCompaniesFetchAll,
  useGetCompanyWithBlueprint,
} from "../api/CompaniesApi";
import { useGetAllBlueprints, useGetBehaviorKits } from "../api/BlueprintsApi";
import { BlueprintMinimalInfo } from "../api/Blueprints";
import { NameType } from "../api/Person";
import { formatDateForEndpoint, uniqueUUID } from "../utils";
import {
  buildRerunChecktypeSelections,
  resolveRerunBlueprintList,
  resolveRerunBlueprintReportType,
} from "./ReportsWizardUtil";
import { Resume } from "../api/Resume";
import { ReportsPendingReviewItem } from "../api/Reports";
import { useCreatePersonEmpty, useGetPersonInfo } from "../api/PersonApi";
import {
  RERUN_PROFILE_PREFILL_PAGE_SIZE,
  useFetchItemsForReview,
} from "../api/ItemsApi";
import { ItemTypes } from "../api/Items";
import { useStagingPerson } from "./useStagingPerson";

export enum REPORT_WIZARD_STEPS {
  BASIC_INFO = 0,
  PERSON_INFO = 1,
  CHECKTYPES = 2,
  ADDITIONAL_INFO = 3,
  DOCUMENT_UPLOAD = 4,
  SUMMARY = 5,
}

export type ReportsWizardStep = {
  idx: number;
  isValid: boolean;
  data:
    | BasicInfoData
    | PersonInfoData
    | ChecktypeData
    | AdditionalInfoData
    | DocumentUploadData
    | null;
  isCompleted: boolean;
  isSummary: boolean;
  label: string;
  isDirty: boolean;
};

const initialSteps: ReportsWizardStep[] = [
  {
    idx: 0,
    isValid: false,
    isDirty: false,
    data: {
      firstName: "",
      middleName: "",
      lastName: "",
      companyUUID: "",
      jobTitle: "",
      clientReportId: "",
    },
    isCompleted: false,
    isSummary: false,
    label: "Basic Info",
  },
  {
    idx: 1,
    isValid: false,
    isDirty: false,
    isCompleted: false,
    isSummary: false,
    label: "Person Info",
    data: {
      aliases: [],
      emails: [],
      phones: [],
      dateOfBirth: "",
      urls: [],
    },
  },
  {
    idx: 2,
    isValid: false,
    isDirty: false,
    data: {
      selectedMisconductChecktype: "",
      selectedThreeSixtyChecktype: "",
      selectedBehaviorKit: "",
      requisitionNumber: "",
      checkedPulseReport: false,
      pulseReportMonths: 0,
      internalQa: undefined,
      clientQa: undefined,
    },
    isCompleted: false,
    isSummary: false,
    label: "Check Types",
  },
  {
    idx: 3,
    isValid: false,
    isDirty: false,
    data: {
      addresses: [],
      jobs: [],
      degrees: [],
    },
    isCompleted: false,
    isSummary: false,
    label: "Additional Info",
  },
  {
    idx: 4,
    isValid: false,
    isDirty: false,
    data: {
      writingSample: "",
    },
    isCompleted: false,
    isSummary: false,
    label: "Document Upload",
  },
  {
    idx: 5,
    isValid: false,
    isDirty: false,
    data: null,
    isCompleted: false,
    isSummary: true,
    label: "Summary",
  },
];

export type BasicInfoData = {
  firstName: string;
  middleName: string;
  lastName: string;
  companyUUID: string;
  jobTitle: string;
  clientReportId: string;
};

export type PersonInfoData = {
  /** Parent subject UUID from rerun prefill — guard only, not staging person UUID. */
  personUuid?: string;
  aliases: {
    firstName: string;
    middleName: string;
    lastName: string;
    name_type: NameType;
  }[];
  emails: string[];
  phones: string[];
  dateOfBirth: string;
  urls: string[];
};

export type AdditionalInfoData = {
  addresses: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }[];
  jobs: {
    title: string;
    company: string;
    startDate?: string;
    endDate?: string;
    country: string;
  }[];
  degrees: {
    degree: string;
    institution: string;
    startDate?: string;
    endDate?: string;
    country: string;
  }[];
};

export type ChecktypeData = {
  selectedMisconductChecktype: string;
  selectedThreeSixtyChecktype: string;
  selectedBehaviorKit: string;
  requisitionNumber: string;
  checkedPulseReport: boolean;
  pulseReportMonths: number;
  internalQa?: boolean;
  clientQa?: boolean;
};

export type DocumentUploadData = {
  writingSample: string;
};

export type ReportsWizardContextType = {
  companyQuery: {
    data: CompaniesApi[] | undefined;
    isLoading: boolean;
    error: unknown;
  };
  blueprintsQuery: {
    data: BlueprintMinimalInfo[] | undefined;
    isLoading: boolean;
    error: unknown;
  };
  steps: ReportsWizardStep[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  nextStep: (isValid: boolean, isCompleted: boolean) => void | Promise<void>;
  previousStep: () => void;
  setBasicInfo: (basicInfo: BasicInfoData) => void;
  selectedCompanyUUID: string | null;
  getBasicInfo: () => BasicInfoData | null;
  setPersonInfo: (personInfo: PersonInfoData) => void;
  getPersonInfo: () => PersonInfoData | null;
  setAdditionalInfo: (additionalInfo: AdditionalInfoData) => void;
  getAdditionalInfo: () => AdditionalInfoData | null;
  setChecktypeInfo: (checktypeInfo: ChecktypeData) => void;
  getChecktypeInfo: () => ChecktypeData | null;
  setValidStep: (step: number, isValid: boolean) => void;
  setIsDirty: (step: number, isDirty: boolean) => void;
  getIsDirty: (step: number) => boolean;
  setDocumentUpload: (documentUpload: DocumentUploadData) => void;
  getDocumentUpload: () => DocumentUploadData | null;
  initialize: (
    rerun?: boolean,
    parent_report?: ReportsPendingReviewItem
  ) => void;
  resumeToSteps: (resume: Resume) => ReportsWizardStep[];
  companyWithBlueprintQuery: {
    data: CompanyWithBlueprintInfo[] | undefined;
    isLoading: boolean;
    error: unknown;
    mutate: (input: CompaniesWithBlueprintInput) => void;
  };
  behaviorKitQuery: {
    data: BehaviorKit[] | undefined;
    isLoading: boolean;
    error: unknown;
    mutate: (companyUUID: string) => void;
  };
  companyWithBlueprint: CompanyWithBlueprintInfo | undefined;
  behaviorKit: BehaviorKit[] | undefined;
  is_rerun: boolean;
  isLoadingRerunInfo: boolean;
  personUUID: string | null;
  ensurePersonAvailable: () => Promise<string | null> | null;
  /** True while `/person/create/empty` is in flight for the staging person. */
  isCreatingStagingPerson: boolean;
  parentReportUUID: string | null;
  /** Company the current check-type settings were last reconciled for. */
  checktypeBoundCompanyUUID: string | null;
  setChecktypeBoundCompanyUUID: (companyUUID: string | null) => void;
};

export const ReportsWizardContext = createContext<ReportsWizardContextType>({
  companyQuery: { data: [], isLoading: false, error: null },
  blueprintsQuery: { data: [], isLoading: false, error: null },
  steps: [],
  currentStep: 0,
  selectedCompanyUUID: null,
  setCurrentStep: () => {},
  nextStep: () => {},
  previousStep: () => {},
  setBasicInfo: () => {},
  getBasicInfo: () => null,
  setPersonInfo: () => null,
  getPersonInfo: () => null,
  setAdditionalInfo: () => null,
  getAdditionalInfo: () => null,
  setChecktypeInfo: () => null,
  getChecktypeInfo: () => null,
  setValidStep: () => {},
  setIsDirty: () => {},
  getIsDirty: () => false,
  setDocumentUpload: () => null,
  getDocumentUpload: () => null,
  initialize: () => {},
  resumeToSteps: () => [],
  companyWithBlueprintQuery: {
    data: [],
    isLoading: false,
    error: null,
    mutate: () => {},
  },
  behaviorKitQuery: {
    data: [],
    isLoading: false,
    error: null,
    mutate: () => {},
  },
  companyWithBlueprint: undefined,
  behaviorKit: undefined,
  is_rerun: false,
  isLoadingRerunInfo: false,
  personUUID: null,
  ensurePersonAvailable: () => null,
  isCreatingStagingPerson: false,
  parentReportUUID: null,
  checktypeBoundCompanyUUID: null,
  setChecktypeBoundCompanyUUID: () => {},
});

export const ReportsWizardProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [currentStep, setCurrentStep] = useState(
    REPORT_WIZARD_STEPS.PERSON_INFO
  );
  const [selectedCompanyUUID, setSelectedCompanyUUID] = useState<string | null>(
    null
  );
  const [checktypeBoundCompanyUUID, setChecktypeBoundCompanyUUID] = useState<
    string | null
  >(null);
  const {
    mutate: getAllCompanies,
    data: companiesInfo,
    isLoading: isLoadingCompanies,
    error: errorCompanies,
  } = useCompaniesFetchAll();
  const {
    mutate: getBlueprints,
    data: blueprintsList,
    isLoading: isLoadingBlueprints,
    error: errorBlueprints,
  } = useGetAllBlueprints();

  const { mutateAsync: getPerson, isLoading: isLoadingPerson } =
    useGetPersonInfo();
  const { mutateAsync: getProfiles, isLoading: isLoadingProfiles } =
    useFetchItemsForReview();

  const companyWithBlueprintQuery = useGetCompanyWithBlueprint();
  const behaviorKitQuery = useGetBehaviorKits();
  const {
    mutateAsync: createPersonEmpty,
    isLoading: createPersonEmptyLoading,
  } = useCreatePersonEmpty();

  useEffect(() => {
    getAllCompanies();
    getBlueprints();
  }, [getAllCompanies, getBlueprints]);

  const [steps, setSteps] = useState<ReportsWizardStep[]>(initialSteps);
  const { personUUID, ensurePersonAvailable, resetStagingPerson } =
    useStagingPerson(
      selectedCompanyUUID,
      createPersonEmpty,
      createPersonEmptyLoading
    );
  const [parentReportUUID, setParentReportUUID] = useState<string | null>(null);
  const [isLoadingParentReport, setIsLoadingParentReport] = useState(false);

  useEffect(() => {
    if (selectedCompanyUUID) {
      companyWithBlueprintQuery.mutate({
        company_uuids: [selectedCompanyUUID],
        blueprintsList: blueprintsList ?? [],
      });
      behaviorKitQuery.mutate(selectedCompanyUUID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyUUID, blueprintsList]);

  const nextStep = async (isValid: boolean, isCompleted: boolean) => {
    let nextStep = currentStep + 1;
    if (nextStep === REPORT_WIZARD_STEPS.ADDITIONAL_INFO) {
      // Empty means the create was invalidated (company changed mid-request)
      // or returned no uuid. Stay on Check Types so later steps keep a person.
      const stagingPersonUUID = await ensurePersonAvailable();
      if (!stagingPersonUUID) {
        return;
      }
    }
    if (nextStep === REPORT_WIZARD_STEPS.DOCUMENT_UPLOAD) {
      const checktypeInfo = getChecktypeInfo();
      if ((checktypeInfo?.selectedThreeSixtyChecktype.length ?? 0) === 0) {
        nextStep = REPORT_WIZARD_STEPS.SUMMARY;
      }
    }
    const data = steps[currentStep];
    data.isValid = isValid;
    data.isCompleted = isCompleted;
    setSteps((prevSteps) => {
      prevSteps[currentStep] = { ...data };
      return prevSteps;
    });
    setCurrentStep(nextStep);
  };

  const previousStep = () => {
    let previousStep = currentStep - 1;
    if (previousStep === REPORT_WIZARD_STEPS.DOCUMENT_UPLOAD) {
      const checktypeInfo = getChecktypeInfo();
      if ((checktypeInfo?.selectedThreeSixtyChecktype.length ?? 0) === 0) {
        previousStep = REPORT_WIZARD_STEPS.ADDITIONAL_INFO;
      }
    }
    setCurrentStep(previousStep);
  };

  function setBasicInfo(basicInfo: BasicInfoData) {
    setSteps((prevSteps) => {
      prevSteps[REPORT_WIZARD_STEPS.BASIC_INFO].data = { ...basicInfo };
      setSelectedCompanyUUID(basicInfo.companyUUID || null);
      return prevSteps;
    });
  }

  function setPersonInfo(personInfo: PersonInfoData) {
    setSteps((prevSteps) => {
      prevSteps[REPORT_WIZARD_STEPS.PERSON_INFO].data = { ...personInfo };
      return prevSteps;
    });
  }

  function getBasicInfo() {
    return steps[REPORT_WIZARD_STEPS.BASIC_INFO].data as BasicInfoData | null;
  }

  function getPersonInfo() {
    return steps[REPORT_WIZARD_STEPS.PERSON_INFO].data as PersonInfoData | null;
  }

  function setAdditionalInfo(additionalInfo: AdditionalInfoData) {
    setSteps((prevSteps) => {
      prevSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].data = {
        ...additionalInfo,
      };
      return prevSteps;
    });
  }

  function getAdditionalInfo() {
    return steps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO]
      .data as AdditionalInfoData | null;
  }

  function setChecktypeInfo(checktypeInfo: ChecktypeData) {
    setSteps((prevSteps) => {
      prevSteps[REPORT_WIZARD_STEPS.CHECKTYPES].data = { ...checktypeInfo };
      return prevSteps;
    });
  }

  function getChecktypeInfo() {
    return steps[REPORT_WIZARD_STEPS.CHECKTYPES].data as ChecktypeData | null;
  }

  function setValidStep(step: number, isValid: boolean) {
    setSteps((prevSteps) => {
      prevSteps[step] = { ...prevSteps[step], isValid };
      return [...prevSteps];
    });
  }

  function setIsDirty(step: number, isDirty: boolean) {
    setSteps((prevSteps) => {
      prevSteps[step] = { ...prevSteps[step], isDirty };
      return [...prevSteps];
    });
  }

  function getIsDirty(step: number) {
    return steps[step].isDirty;
  }

  function setDocumentUpload(documentUpload: DocumentUploadData) {
    setSteps((prevSteps) => {
      prevSteps[REPORT_WIZARD_STEPS.DOCUMENT_UPLOAD].data = {
        ...documentUpload,
      };
      return prevSteps;
    });
  }

  function getDocumentUpload() {
    return steps[REPORT_WIZARD_STEPS.DOCUMENT_UPLOAD]
      .data as DocumentUploadData | null;
  }

  async function initialize(
    rerun?: boolean,
    parent_report?: ReportsPendingReviewItem
  ) {
    const newSteps = JSON.parse(JSON.stringify(initialSteps));
    setCurrentStep(REPORT_WIZARD_STEPS.PERSON_INFO);
    setIsRerun(false);
    resetStagingPerson();
    setSelectedCompanyUUID(null);
    setParentReportUUID(null);
    setChecktypeBoundCompanyUUID(null);

    if (rerun && parent_report?.report_uuid) {
      setParentReportUUID(parent_report?.report_uuid);
      setIsLoadingParentReport(true);
      setIsRerun(true);
      if (!parent_report?.report_uuid) {
        return;
      }
      setSelectedCompanyUUID(parent_report?.company_uuid);
      setChecktypeBoundCompanyUUID(parent_report?.company_uuid);
      const companyWithBlueprint = await companyWithBlueprintQuery.mutateAsync({
        company_uuids: [parent_report?.company_uuid],
        blueprintsList: blueprintsList ?? [],
      });
      const person = await getPerson(parent_report?.person_uuid);
      const profiles = await getProfiles({
        report_uuid: parent_report.report_uuid,
        item_type_name: [ItemTypes.SOCIAL_MEDIA_PROFILE],
        // Same as PersonForm hybrid rerun: default page_size is 10 newest-first;
        // fetch every page so older parent in_report profiles reach the form.
        page_size: RERUN_PROFILE_PREFILL_PAGE_SIZE,
        fetch_all: true,
      });

      // transform parent report to steps data
      const mainPerson = person?.names.find(
        (name) => name.name_type === NameType.MAIN
      );

      newSteps[REPORT_WIZARD_STEPS.BASIC_INFO].data = {
        firstName: mainPerson?.first_name || "",
        middleName: mainPerson?.middle_name || "",
        lastName: mainPerson?.last_name || "",
        companyUUID: parent_report?.company_uuid || "",
        jobTitle: parent_report?.job_title || "",
        clientReportId: parent_report?.client_report_id || "",
      };
      newSteps[REPORT_WIZARD_STEPS.BASIC_INFO].isDirty = true;
      newSteps[REPORT_WIZARD_STEPS.BASIC_INFO].isValid = true;
      newSteps[REPORT_WIZARD_STEPS.BASIC_INFO].isCompleted = true;

      newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].data = {
        // Parent subject UUID for display/prefill only — hybrid rerun PATCH must use
        // ReportsWizardProvider.personUUID (staging from create/empty), not this field.
        // See docs/HYBRID_RERUN.md.
        personUuid: parent_report?.person_uuid || "",
        aliases: person?.names
          .filter((name) => name.name_type !== NameType.MAIN)
          .map((name) => ({
            firstName: name.first_name || "",
            middleName: name.middle_name || "",
            lastName: name.last_name || "",
            name_type: name.name_type,
            key: uniqueUUID(),
          })),
        emails: person?.emails.map((email) => email.address) || [],
        phones: person?.phones.map((phone) => phone.raw_number) || [],
        dateOfBirth: person?.birth_date || "",
        urls:
          profiles?.items
            .filter((item) => item.item.in_report)
            .map((profile) => profile.item.content.url) || [],
      };
      newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].isDirty = true;
      newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].isValid = true;
      newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].isCompleted = true;

      newSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].data = {
        addresses: person?.addresses.map((address) => ({
          street: address.line1 + " " + address.line2 || "",
          city: address.city || "",
          state: address.state || "",
          zip: address.zip || "",
          country: address.country || "",
        })),
        jobs: person?.jobs.map((job) => ({
          title: job.title || "",
          company: job.employer || "",
          startDate: formatDateForEndpoint(job.start_date) || undefined,
          endDate: formatDateForEndpoint(job.end_date) || undefined,
          country: job.country || "",
        })),
        degrees: person?.degrees.map((degree) => ({
          degree: degree.name || "",
          institution: degree.educator || "",
          startDate: formatDateForEndpoint(degree.start_date) || undefined,
          endDate: formatDateForEndpoint(degree.end_date) || undefined,
          country: degree.country || "",
        })),
      };
      newSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].isDirty = true;
      newSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].isValid = true;
      newSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].isCompleted = true;
      const blueprintUuid = parent_report?.blueprint_uuid || "";
      const blueprintList = resolveRerunBlueprintList(
        companyWithBlueprint?.[0]?.blueprintsInfo,
        blueprintsList
      );
      const reportType = resolveRerunBlueprintReportType(
        blueprintUuid,
        blueprintList,
        parent_report
      );
      const checktypeSelections = buildRerunChecktypeSelections(
        blueprintUuid,
        reportType
      );
      newSteps[REPORT_WIZARD_STEPS.CHECKTYPES].data = {
        ...checktypeSelections,
        selectedBehaviorKit: parent_report?.behavior_kit_uuid || "",
        requisitionNumber: "",
        clientQa: companyWithBlueprint?.[0]?.client_qa || false,
        internalQa: companyWithBlueprint?.[0]?.internal_qa || false,
      };
      newSteps[REPORT_WIZARD_STEPS.CHECKTYPES].isDirty = true;
      newSteps[REPORT_WIZARD_STEPS.CHECKTYPES].isValid = true;
      newSteps[REPORT_WIZARD_STEPS.CHECKTYPES].isCompleted = true;
      newSteps[REPORT_WIZARD_STEPS.SUMMARY].isCompleted = true;
      setCurrentStep(REPORT_WIZARD_STEPS.SUMMARY);
      setIsLoadingParentReport(false);
    }
    setSteps(newSteps);
  }

  function resumeToSteps(resume: Resume): ReportsWizardStep[] {
    // Create a deep copy of initialSteps to ensure we're not mutating the template
    const newSteps = JSON.parse(
      JSON.stringify(initialSteps)
    ) as ReportsWizardStep[];

    // --- BASIC_INFO ---
    if (resume.name || resume.profession || resume.report_id || resume.jobs) {
      const basicInfo = getBasicInfo();
      const basicInfoData: BasicInfoData = {
        firstName: resume.name?.first || "",
        middleName: resume.name?.middle || "",
        lastName: resume.name?.last || "",
        companyUUID: basicInfo?.companyUUID || "",
        jobTitle: resume.profession || resume.jobs?.[0]?.job_title || "",
        clientReportId: basicInfo?.clientReportId || "",
      };
      newSteps[REPORT_WIZARD_STEPS.BASIC_INFO].data = basicInfoData;
      newSteps[REPORT_WIZARD_STEPS.BASIC_INFO].isDirty = true;
      // isValid will be determined by the step component/validation logic
    }

    // --- PERSON_INFO ---
    if (resume.emails || resume.phone_numbers || resume.websites) {
      const personInfoData: PersonInfoData = {
        aliases: [], // The Resume type doesn't have a direct mapping for aliases with NameType
        emails: resume.emails || [],
        phones:
          (resume.phone_numbers
            ?.map((p) => p.formatted_number || p.raw_text)
            .filter(Boolean) as string[]) || [],
        dateOfBirth: "", // Date of birth is not available in the Resume object
        urls: resume.websites || [],
      };
      newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].data = personInfoData;
      newSteps[REPORT_WIZARD_STEPS.PERSON_INFO].isDirty = true;
    }

    // --- ADDITIONAL_INFO ---
    if (resume.addresses || resume.jobs || resume.education) {
      const additionalInfoData: AdditionalInfoData = {
        addresses:
          resume.addresses?.map((addr) => ({
            street: addr.raw_input || addr.formatted || "",
            city: addr.city || "",
            state: addr.state || "",
            zip: "", // Zip code is not available in the Resume.Address structure
            country: addr.country || "",
          })) || [],
        jobs:
          resume.jobs?.map((job) => ({
            title: job.job_title || "",
            company: job.organization || "",
            startDate: formatDateForEndpoint(job.start_date) || undefined,
            endDate: formatDateForEndpoint(job.end_date) || undefined,
            country: job.location?.country || "",
          })) || [],
        degrees:
          resume.education?.map((edu) => ({
            degree:
              edu.accreditation?.input_str ||
              edu.accreditation?.match_str ||
              "",
            institution: edu.organization || "",
            startDate: undefined, // Start date for education is not in Resume.Education
            endDate: formatDateForEndpoint(edu.completion_date) || undefined,
            country: edu.location?.country || "",
          })) || [],
      };
      newSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].data = additionalInfoData;
      newSteps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO].isDirty = true;
    }

    setSteps(newSteps);
    return newSteps;
  }

  const companyWithBlueprint = companyWithBlueprintQuery.data?.[0];
  const behaviorKit = behaviorKitQuery.data;
  const [is_rerun, setIsRerun] = useState(false);

  return (
    <ReportsWizardContext.Provider
      value={{
        companyQuery: {
          data: companiesInfo,
          isLoading: isLoadingCompanies,
          error: errorCompanies,
        },
        blueprintsQuery: {
          data: blueprintsList,
          isLoading: isLoadingBlueprints,
          error: errorBlueprints,
        },
        steps,
        currentStep,
        setCurrentStep,
        nextStep,
        previousStep,
        setBasicInfo,
        selectedCompanyUUID,
        getBasicInfo,
        setPersonInfo,
        getPersonInfo,
        setAdditionalInfo,
        getAdditionalInfo,
        setChecktypeInfo,
        getChecktypeInfo,
        setValidStep,
        setIsDirty,
        getIsDirty,
        setDocumentUpload,
        getDocumentUpload,
        initialize,
        resumeToSteps,
        companyWithBlueprintQuery,
        behaviorKitQuery,
        companyWithBlueprint,
        behaviorKit,
        is_rerun,
        parentReportUUID: parentReportUUID,
        checktypeBoundCompanyUUID,
        setChecktypeBoundCompanyUUID,
        isLoadingRerunInfo:
          isLoadingPerson || isLoadingProfiles || isLoadingParentReport,
        personUUID: personUUID,
        ensurePersonAvailable,
        isCreatingStagingPerson: createPersonEmptyLoading,
      }}
    >
      {children}
    </ReportsWizardContext.Provider>
  );
};

export const useReportsWizard = () => {
  return useContext(ReportsWizardContext);
};
